import { useState } from 'react';
import { supabase } from '../lib/supabaseClient';

/**
 * useMediaUpload Hook
 * Handles file uploads to Supabase Storage with validation, progress tracking, and signed URL generation
 *
 * @returns {Object} Upload utilities
 * @returns {Function} uploadFile - Upload file to storage
 * @returns {Function} deleteFile - Delete file from storage
 * @returns {Function} getSignedUrl - Get signed URL for private file
 * @returns {boolean} uploading - Upload in progress
 * @returns {number} progress - Upload progress (0-100)
 * @returns {string} error - Error message if any
 */
const useMediaUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  /**
   * Validate file before upload
   * @param {File} file - File to validate
   * @param {string} type - Expected type ('video' or 'image')
   * @param {number} maxSize - Maximum file size in bytes
   * @returns {Object} Validation result
   */
  const validateFile = (file, type, maxSize) => {
    // Check if file exists
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    // Check file type
    const validVideoTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
    const validImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const validTypes = type === 'video' ? validVideoTypes : validImageTypes;

    if (!validTypes.includes(file.type)) {
      const expectedFormats =
        type === 'video' ? 'MP4, MOV, WebM, or AVI' : 'JPG, PNG, WebP, or GIF';
      return {
        valid: false,
        error: `Invalid file type. Please upload ${expectedFormats} files.`,
      };
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return {
        valid: false,
        error: `File is too large (${fileSizeMB}MB). Maximum size is ${maxSizeMB}MB.`,
      };
    }

    return { valid: true, error: null };
  };

  /**
   * Generate unique filename with organization and timestamp
   * @param {string} orgId - Organization ID
   * @param {string} drillId - Drill ID (optional, for drill-specific paths)
   * @param {string} extension - File extension
   * @param {string} prefix - Filename prefix (e.g., 'video', 'thumbnail')
   * @returns {string} Generated filename path
   */
  const generateFilename = (orgId, drillId, extension, prefix = 'file') => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);

    if (drillId) {
      return `${orgId}/${drillId}/${timestamp}-${prefix}-${random}.${extension}`;
    }

    return `${orgId}/${timestamp}-${prefix}-${random}.${extension}`;
  };

  /**
   * Upload file to Supabase Storage
   * @param {File} file - File to upload
   * @param {string} bucket - Storage bucket name
   * @param {string} path - File path in bucket
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<Object>} Upload result with path and signed URL
   */
  const uploadFile = async (file, bucket, path, onProgress = null) => {
    setUploading(true);
    setProgress(0);
    setError('');

    try {
      // Upload file to storage
      const { data, error: uploadError } = await supabase.storage.from(bucket).upload(path, file, {
        cacheControl: '3600',
        upsert: false, // Don't overwrite existing files
      });

      if (uploadError) {
        throw new Error(uploadError.message);
      }

      // Simulate progress for better UX (Supabase doesn't provide real-time progress)
      setProgress(50);

      // Generate signed URL for private bucket access (1 year validity)
      const { data: urlData, error: urlError } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, 31536000); // 365 days in seconds

      if (urlError) {
        throw new Error(`Failed to generate signed URL: ${urlError.message}`);
      }

      setProgress(100);

      return {
        path: data.path,
        signedUrl: urlData.signedUrl,
        success: true,
      };
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
      return {
        path: null,
        signedUrl: null,
        success: false,
        error: err.message,
      };
    } finally {
      setUploading(false);
      setTimeout(() => {
        setProgress(0);
        setError('');
      }, 2000); // Clear progress and error after 2 seconds
    }
  };

  /**
   * Delete file from Supabase Storage
   * @param {string} bucket - Storage bucket name
   * @param {string} path - File path in bucket
   * @returns {Promise<Object>} Deletion result
   */
  const deleteFile = async (bucket, path) => {
    if (!path) {
      return { success: true, message: 'No file to delete' };
    }

    try {
      const { error: deleteError } = await supabase.storage.from(bucket).remove([path]);

      if (deleteError) {
        throw new Error(deleteError.message);
      }

      return {
        success: true,
        message: 'File deleted successfully',
      };
    } catch (err) {
      console.error('Delete error:', err);
      return {
        success: false,
        error: err.message,
      };
    }
  };

  /**
   * Get signed URL for existing file in private bucket
   * @param {string} bucket - Storage bucket name
   * @param {string} path - File path in bucket
   * @param {number} expiresIn - URL validity in seconds (default: 1 year)
   * @returns {Promise<string|null>} Signed URL or null if error
   */
  const getSignedUrl = async (bucket, path, expiresIn = 31536000) => {
    if (!path) {
      return null;
    }

    try {
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);

      if (error) {
        console.error('Error generating signed URL:', error);
        return null;
      }

      return data.signedUrl;
    } catch (err) {
      console.error('Signed URL error:', err);
      return null;
    }
  };

  /**
   * Check if file exists in storage
   * @param {string} bucket - Storage bucket name
   * @param {string} path - File path in bucket
   * @returns {Promise<boolean>} True if file exists
   */
  const fileExists = async (bucket, path) => {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .list(path.split('/').slice(0, -1).join('/'), {
          limit: 1,
          search: path.split('/').pop(),
        });

      if (error) {
        return false;
      }

      return data && data.length > 0;
    } catch (err) {
      console.error('File exists check error:', err);
      return false;
    }
  };

  return {
    uploadFile,
    deleteFile,
    getSignedUrl,
    fileExists,
    validateFile,
    generateFilename,
    uploading,
    progress,
    error,
  };
};

export default useMediaUpload;
